import { map } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faThumbTack } from "@fortawesome/pro-solid-svg-icons";
import Modal from "../../shared/Modal";
import StaticTimeAgo from "../../shared/StaticTimeAgo";
import { StickySibling } from "./api.ts";

type ReplaceStickiesModalArgs = {
  siblings: StickySibling[];
  // true: unpin the listed posts, false: keep them and pin additionally
  onDecision: (replace: boolean) => void;
  onClose: () => void;
};

// Asked before pinning while other posts of the same context are pinned
// (#283). Cancel, Esc and the close icon all just close — the parent's
// onClose must stay side-effect free because the shared Modal also fires it
// when it is unmounted after a decision.
export default function ReplaceStickiesModal({
  siblings,
  onDecision,
  onClose,
}: ReplaceStickiesModalArgs) {
  return (
    <Modal
      static
      title={I18n.t("js.activitystreams.sticky.replace_modal.title")}
      onClose={onClose}
      useMinHeightMax
    >
      <div className="flex flex-col gap-4">
        <p className="mb-0">
          {I18n.t("js.activitystreams.sticky.replace_modal.description")}
        </p>
        {/* scrolls on its own: a never tidied network can carry dozens of pins */}
        <ul className="replace-stickies-list list-unstyled mb-2 flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
          {map(siblings, (sibling) => (
            <li
              key={sibling.id}
              className="border-box px-3 py-2 flex gap-3 items-start border-primary/25 bg-primary/10"
            >
              <FontAwesomeIcon
                className="text-primary mt-1"
                icon={sibling.sticky === "regular" ? faThumbTack : faCheck}
              />
              <div className="min-w-0 flex flex-col">
                <a
                  href={sibling.url}
                  className="font-medium line-clamp-2 break-words"
                  target="_blank"
                  rel="noreferrer"
                >
                  {sibling.teaser || I18n.t("js.activitystreams.sticky.hint")}
                </a>
                <span className="text-muted text-sm">
                  <StaticTimeAgo date={sibling.published} />
                  {sibling.activitystream_names.length > 0 &&
                    ` · ${sibling.activitystream_names.join(", ")}`}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="form-actions modal-footer flex flex-wrap gap-2 justify-end">
        <button type="button" className="btn btn-link" data-bs-dismiss="modal">
          {I18n.t("js.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-light"
          onClick={() => onDecision(false)}
        >
          {I18n.t("js.activitystreams.sticky.replace_modal.add")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onDecision(true)}
        >
          {I18n.t("js.activitystreams.sticky.replace_modal.replace")}
        </button>
      </div>
    </Modal>
  );
}
